/**
 * Case Summary Service
 * Frontend service for interacting with case summary API endpoints
 */

import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get the current active case summary for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {boolean} includeHistory - Whether to include version history
 * @returns {Promise<{summary: Object|null, history?: Array}>}
 */
export async function getCaseSummary(patientId, facilityId, includeHistory = false) {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams({
      facilityId,
      ...(includeHistory && { includeHistory: 'true' }),
    });

    const response = await fetch(
      `${API_BASE}/clinical/patients/${patientId}/case-summary?${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get case summary');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting case summary:', error);
    throw error;
  }
}

/**
 * Get case summary version history for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {number} limit - Maximum number of versions to return
 * @returns {Promise<{history: Array, total: number}>}
 */
export async function getCaseSummaryHistory(patientId, facilityId, limit = 10) {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams({
      facilityId,
      limit: limit.toString(),
    });

    const response = await fetch(
      `${API_BASE}/clinical/patients/${patientId}/case-summary/history?${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get case summary history');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting case summary history:', error);
    throw error;
  }
}

/**
 * Generate or regenerate a case summary using AI
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {boolean} forceRegenerate - Force regeneration even if summary exists
 * @returns {Promise<{summary: Object, isNew: boolean, generatedAt: string}>}
 */
export async function generateCaseSummary(patientId, facilityId, forceRegenerate = false) {
  try {
    const token = await getAuthToken();

    const response = await fetch(
      `${API_BASE}/clinical/patients/${patientId}/case-summary/generate`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          facilityId,
          forceRegenerate,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate case summary');
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating case summary:', error);
    throw error;
  }
}

/**
 * Generate AI-enhanced case summary content via the AI API
 * This generates the narrative portions using Gemini
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} AI-generated summary content
 */
export async function generateAICaseSummary(patientId, facilityId) {
  try {
    const token = await getAuthToken();

    const response = await fetch(
      `${API_BASE}/aiChat/ai/case-summary/generate`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId,
          facilityId,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate AI case summary');
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating AI case summary:', error);
    throw error;
  }
}

/**
 * Update an existing case summary (manual edits)
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{summary: Object, updatedAt: string}>}
 */
export async function updateCaseSummary(patientId, facilityId, updates) {
  try {
    const token = await getAuthToken();

    const response = await fetch(
      `${API_BASE}/clinical/patients/${patientId}/case-summary`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          facilityId,
          updates,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update case summary');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating case summary:', error);
    throw error;
  }
}

/**
 * Archive a case summary
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function archiveCaseSummary(patientId, facilityId) {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams({ facilityId });

    const response = await fetch(
      `${API_BASE}/clinical/patients/${patientId}/case-summary?${queryParams}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to archive case summary');
    }

    return await response.json();
  } catch (error) {
    console.error('Error archiving case summary:', error);
    throw error;
  }
}

/**
 * Check if a case summary needs refresh
 * @param {Object} summary - The case summary object
 * @returns {boolean} Whether the summary needs to be refreshed
 */
export function needsRefresh(summary) {
  if (!summary) return true;
  return summary.needsRefresh === true;
}

/**
 * Format a case summary section for display
 * @param {Array|string} content - The section content
 * @param {string} emptyMessage - Message to show if content is empty
 * @returns {string} Formatted content
 */
export function formatSectionContent(content, emptyMessage = 'Not documented') {
  if (!content) return emptyMessage;
  if (Array.isArray(content)) {
    return content.length > 0 ? content.join(', ') : emptyMessage;
  }
  return content || emptyMessage;
}

/**
 * Get the status badge color for a case summary status
 * @param {string} status - The summary status
 * @returns {string} MUI color name
 */
export function getStatusColor(status) {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
      return 'default';
    default:
      return 'primary';
  }
}

const caseSummaryService = {
  getCaseSummary,
  getCaseSummaryHistory,
  generateCaseSummary,
  generateAICaseSummary,
  updateCaseSummary,
  archiveCaseSummary,
  needsRefresh,
  formatSectionContent,
  getStatusColor,
};

export default caseSummaryService;
